import { useMutation, useQueryClient } from 'react-query';
import type { PaymentMethodViewDto } from '@subscriptions/application/dtos';
import { getSubscriptionBlockUseCases } from '@subscriptions/composition';
import type { GenerateReferenceMode } from '@subscriptions/domain/types/SubscriptionsConst';

/**
 * Hook for generating the SPEI CLABE reference for a subscription block.
 *
 * Resolves with the full attached payment method (CLABE in `providerPublicId`)
 * so the SPEI confirmation screen can render directly. On success, invalidates
 * the cached `['subscriptions', 'blocks', 'detail', blockUuid]` query (the key
 * used by `useSubscriptionBlock`) so downstream views refetch.
 *
 * @returns React Query mutation result
 */
export const useGenerateBlockReference = () => {
  const queryClient = useQueryClient();
  const { generateReferenceSubscriptionBlock } = getSubscriptionBlockUseCases();

  // `mode` asks for the reactivation CLABE instead of the regular one.
  return useMutation<PaymentMethodViewDto, Error, { blockUuid: string; mode?: GenerateReferenceMode }>(
    ({ blockUuid, mode }) => generateReferenceSubscriptionBlock.execute(blockUuid, mode),
    {
      onSuccess: (_, { blockUuid }) => {
        queryClient.invalidateQueries(['subscriptions', 'blocks', 'detail', blockUuid]);
      },
    },
  );
};
